import React, { useState, useEffect } from 'react';
import ToolLayout from '../components/ToolLayout';

interface ColorData {
  hex: string;
  rgb: { r: number; g: number; b: number };
  hsl: { h: number; s: number; l: number };
  hsv: { h: number; s: number; v: number };
  cmyk: { c: number; m: number; y: number; k: number };
}

const ColorPicker: React.FC = () => {
  const [color, setColor] = useState('#3B82F6');
  const [colorData, setColorData] = useState<ColorData | null>(null);
  const [inputValue, setInputValue] = useState('');
  const [inputFormat, setInputFormat] = useState<'hex' | 'rgb' | 'hsl'>('hex');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    updateColorData(color);
  }, [color]);

  const updateColorData = (hexColor: string) => {
    try {
      const rgb = hexToRgb(hexColor);
      if (!rgb) {throw new Error('Invalid color');}
      
      const hsl = rgbToHsl(rgb.r, rgb.g, rgb.b);
      const hsv = rgbToHsv(rgb.r, rgb.g, rgb.b);
      const cmyk = rgbToCmyk(rgb.r, rgb.g, rgb.b);

      setColorData({
        hex: hexColor.toUpperCase(),
        rgb,
        hsl,
        hsv,
        cmyk
      });
      setError('');
    } catch (err) {
      setError('Invalid color value');
      setColorData(null);
    }
  };

  const hexToRgb = (hex: string) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
      r: parseInt(result[1], 16),
      g: parseInt(result[2], 16),
      b: parseInt(result[3], 16)
    } : null;
  };

  const rgbToHsl = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;

    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      l: Math.round(l * 100)
    };
  };

  const rgbToHsv = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const v = max;
    const d = max - min;
    const s = max === 0 ? 0 : d / max;
    let h = 0;

    if (max !== min) {
      switch (max) {
        case r: h = (g - b) / d + (g < b ? 6 : 0); break;
        case g: h = (b - r) / d + 2; break;
        case b: h = (r - g) / d + 4; break;
      }
      h /= 6;
    }

    return {
      h: Math.round(h * 360),
      s: Math.round(s * 100),
      v: Math.round(v * 100)
    };
  };

  const rgbToCmyk = (r: number, g: number, b: number) => {
    r /= 255;
    g /= 255;
    b /= 255;

    const k = 1 - Math.max(r, Math.max(g, b));
    const c = k < 1 ? (1 - r - k) / (1 - k) : 0;
    const m = k < 1 ? (1 - g - k) / (1 - k) : 0;
    const y = k < 1 ? (1 - b - k) / (1 - k) : 0;

    return {
      c: Math.round(c * 100),
      m: Math.round(m * 100),
      y: Math.round(y * 100),
      k: Math.round(k * 100)
    };
  };

  const hslToHex = (h: number, s: number, l: number): string => {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs((h / 60) % 2 - 1));
    const m = l - c/2;
    let r = 0, g = 0, b = 0;

    if (0 <= h && h < 60) {
      r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
      r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
      r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
      r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
      r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
      r = c; g = 0; b = x;
    }

    r = Math.round((r + m) * 255);
    g = Math.round((g + m) * 255);
    b = Math.round((b + m) * 255);

    return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
  };

  const parseColorInput = () => {
    setError('');
    setSuccess('');

    if (!inputValue.trim()) {
      setError('Vui lòng nhập color value');
      return;
    }

    try {
      let hexColor = '';

      if (inputFormat === 'hex') {
        hexColor = inputValue.startsWith('#') ? inputValue : '#' + inputValue;
        if (!/^#[0-9A-F]{6}$/i.test(hexColor)) {
          throw new Error('Invalid hex format');
        }
      } else if (inputFormat === 'rgb') {
        const match = inputValue.match(/rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/i) ||
                     inputValue.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
        
        if (!match) {throw new Error('Invalid RGB format');}
        
        const r = parseInt(match[1]);
        const g = parseInt(match[2]);
        const b = parseInt(match[3]);
        
        if (r > 255 || g > 255 || b > 255) {throw new Error('RGB values must be 0-255');}
        
        hexColor = '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
      } else if (inputFormat === 'hsl') {
        const match = inputValue.match(/hsl\s*\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/i) ||
                     inputValue.match(/(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/);
        
        if (!match) {throw new Error('Invalid HSL format');}
        
        const h = parseInt(match[1]);
        const s = parseInt(match[2]);
        const l = parseInt(match[3]);
        
        if (h > 360 || s > 100 || l > 100) {throw new Error('Invalid HSL range');}
        
        hexColor = hslToHex(h, s, l);
      }

      setColor(hexColor);
      setSuccess('Color đã được update thành công! 🎨');
      setInputValue('');
    } catch (err) {
      setError('Format color không hợp lệ: ' + (err as Error).message);
    }
  };

  const copyToClipboard = async (text: string, format: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSuccess(`Đã copy ${format} vào clipboard!`);
    } catch (err) {
      setError('Không thể copy vào clipboard');
    }
  };

  const generatePalette = () => {
    if (!colorData) {return [];}
    
    const baseHsl = colorData.hsl;
    return [
      hslToHex(baseHsl.h, Math.max(baseHsl.s - 20, 0), Math.min(baseHsl.l + 30, 100)), // Lighter
      hslToHex(baseHsl.h, Math.max(baseHsl.s - 10, 0), Math.min(baseHsl.l + 15, 100)), // Light
      color, // Original
      hslToHex(baseHsl.h, Math.min(baseHsl.s + 10, 100), Math.max(baseHsl.l - 15, 0)), // Dark
      hslToHex(baseHsl.h, Math.min(baseHsl.s + 20, 100), Math.max(baseHsl.l - 30, 0)), // Darker
    ];
  };

  return (
    <ToolLayout
      title="Color Picker"
      description="Chuyển đổi giữa các format màu: HEX, RGB, HSL, HSV, CMYK"
    >
      {/* Color Picker */}
      <div className="color-picker-grid">

        {/* Left Column - Picker & Input */}
        <div>
          <div className="input-group">
            <label className="input-label">🎨 Color Picker:</label>
            <div className="color-picker-display">
              <input
                type="color"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="color-input"
              />
              <div className="color-preview" style={{ backgroundColor: color }} />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">✏️ Nhập Color Value:</label>
            <div className="format-select-row">
              <select
                value={inputFormat}
                onChange={(e) => setInputFormat(e.target.value as 'hex' | 'rgb' | 'hsl')}
                className="format-select"
              >
                <option value="hex">HEX</option>
                <option value="rgb">RGB</option>
                <option value="hsl">HSL</option>
              </select>
              <button
                className="action-button"
                onClick={parseColorInput}
                style={{ fontSize: '0.8rem', padding: '0.5rem 1rem' }}
              >
                Parse
              </button>
            </div>
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={
                inputFormat === 'hex' ? '#FF5733' :
                inputFormat === 'rgb' ? 'rgb(255, 87, 51) hoặc 255, 87, 51' :
                'hsl(9, 100%, 60%) hoặc 9, 100, 60'
              }
              className="color-text-input"
            />
          </div>
        </div>

        {/* Right Column - Color Values */}
        <div>
          {colorData && (
            <div className="input-group">
              <label className="input-label">📋 Color Values:</label>
              <div className="color-values-grid">

                {/* HEX */}
                <div className="color-value-item">
                  <div>
                    <strong className="color-value-label">HEX:</strong>
                    <code className="color-value-code">
                      {colorData.hex}
                    </code>
                  </div>
                  <button
                    className="action-button copy-btn"
                    onClick={() => copyToClipboard(colorData.hex, 'HEX')}
                  >
                    📋
                  </button>
                </div>

                {/* RGB */}
                <div className="color-value-item">
                  <div>
                    <strong className="color-value-label">RGB:</strong>
                    <code className="color-value-code">
                      rgb({colorData.rgb.r}, {colorData.rgb.g}, {colorData.rgb.b})
                    </code>
                  </div>
                  <button
                    className="action-button copy-btn"
                    onClick={() => copyToClipboard(`rgb(${colorData.rgb.r}, ${colorData.rgb.g}, ${colorData.rgb.b})`, 'RGB')}
                  >
                    📋
                  </button>
                </div>

                {/* HSL */}
                <div className="color-value-item">
                  <div>
                    <strong className="color-value-label">HSL:</strong>
                    <code className="color-value-code">
                      hsl({colorData.hsl.h}, {colorData.hsl.s}%, {colorData.hsl.l}%)
                    </code>
                  </div>
                  <button
                    className="action-button copy-btn"
                    onClick={() => copyToClipboard(`hsl(${colorData.hsl.h}, ${colorData.hsl.s}%, ${colorData.hsl.l}%)`, 'HSL')}
                  >
                    📋
                  </button>
                </div>

                {/* HSV */}
                <div className="color-value-item">
                  <div>
                    <strong className="color-value-label">HSV:</strong>
                    <code className="color-value-code">
                      hsv({colorData.hsv.h}, {colorData.hsv.s}%, {colorData.hsv.v}%)
                    </code>
                  </div>
                  <button
                    className="action-button copy-btn"
                    onClick={() => copyToClipboard(`hsv(${colorData.hsv.h}, ${colorData.hsv.s}%, ${colorData.hsv.v}%)`, 'HSV')}
                  >
                    📋
                  </button>
                </div>

                {/* CMYK */}
                <div className="color-value-item">
                  <div>
                    <strong className="color-value-label">CMYK:</strong>
                    <code className="color-value-code">
                      cmyk({colorData.cmyk.c}%, {colorData.cmyk.m}%, {colorData.cmyk.y}%, {colorData.cmyk.k}%)
                    </code>
                  </div>
                  <button
                    className="action-button copy-btn"
                    onClick={() => copyToClipboard(`cmyk(${colorData.cmyk.c}%, ${colorData.cmyk.m}%, ${colorData.cmyk.y}%, ${colorData.cmyk.k}%)`, 'CMYK')}
                  >
                    📋
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Color Palette */}
      {colorData && (
        <div className="color-palette-section">
          <h3 className="palette-title">
            🎨 Color Palette:
          </h3>
          <div className="palette-colors">
            {generatePalette().map((paletteColor, index) => (
              <div
                key={index}
                onClick={() => setColor(paletteColor)}
                className="palette-color"
                style={{
                  backgroundColor: paletteColor,
                  boxShadow: paletteColor === color ? '0 0 0 3px var(--accent-primary)' : 'none'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                }}
                title={paletteColor}
              />
            ))}
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="color-info-section">
        <h4 className="color-info-title">
          🎨 Color Format Guide:
        </h4>
        <div className="color-info-content">
          <div className="color-info-grid">
            <div>
              <p className="color-info-item">
                <strong>HEX:</strong> #FF5733
              </p>
              <p className="color-info-item">
                <strong>RGB:</strong> rgb(255, 87, 51)
              </p>
              <p className="color-info-item">
                <strong>HSL:</strong> hsl(9, 100%, 60%)
              </p>
            </div>
            <div>
              <p className="color-info-item">
                <strong>HSV:</strong> Hue, Saturation, Value
              </p>
              <p className="color-info-item">
                <strong>CMYK:</strong> Print colors (Cyan, Magenta, Yellow, Key/Black)
              </p>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .color-picker-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 2rem;
        }

        .color-picker-display {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 1rem;
        }

        .color-input {
          width: 80px;
          height: 80px;
          border: none;
          border-radius: 8px;
          cursor: pointer;
          box-shadow: var(--shadow-sm);
        }

        .color-preview {
          width: 120px;
          height: 80px;
          border-radius: 8px;
          border: 2px solid var(--border-color);
          box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.1);
        }

        .format-select-row {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
        }

        .format-select {
          padding: 0.5rem;
          border-radius: 4px;
          border: 2px solid var(--border-color);
          background: var(--bg-primary);
          color: var(--text-primary);
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .format-select:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .color-text-input {
          width: 100%;
          padding: 0.75rem;
          border: 2px solid var(--border-color);
          border-radius: 6px;
          background: var(--bg-primary);
          color: var(--text-primary);
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          font-size: 0.9375rem;
          transition: all 0.3s ease;
        }

        .color-text-input:focus {
          outline: none;
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 3px var(--accent-light);
        }

        .color-text-input::placeholder {
          color: var(--text-tertiary);
        }

        .color-values-grid {
          display: grid;
          gap: 0.75rem;
        }

        .color-value-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0.75rem;
          background: var(--bg-secondary);
          border-radius: 6px;
          border: 2px solid var(--border-color);
          transition: all 0.3s ease;
        }

        .color-value-item:hover {
          border-color: var(--accent-primary);
          box-shadow: var(--shadow-sm);
        }

        .color-value-label {
          color: var(--text-primary);
          font-weight: 600;
        }

        .color-value-code {
          margin-left: 0.5rem;
          font-family: Monaco, Menlo, Ubuntu Mono, monospace;
          color: var(--text-secondary);
        }

        .copy-btn {
          font-size: 0.75rem !important;
          padding: 0.4rem 0.8rem !important;
        }

        .color-palette-section {
          margin-top: 2rem;
        }

        .palette-title {
          color: var(--text-primary);
          margin-bottom: 1rem;
          font-size: 1.25rem;
          font-weight: 600;
        }

        .palette-colors {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .palette-color {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          border: 2px solid var(--border-color);
          cursor: pointer;
          transition: transform 0.2s ease;
        }

        .color-info-section {
          margin-top: 2rem;
          padding: 1.5rem;
          background: var(--accent-light);
          border-radius: 12px;
          border: 2px solid var(--accent-primary);
        }

        .color-info-title {
          margin: 0 0 1rem 0;
          color: var(--accent-primary);
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 1.125rem;
          font-weight: 700;
        }

        .color-info-content {
          color: var(--text-primary);
          font-size: 0.875rem;
          line-height: 1.6;
        }

        .color-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1rem;
        }

        .color-info-item {
          margin: 0 0 0.5rem 0;
        }

        .color-info-item strong {
          font-weight: 600;
        }

        @media (max-width: 768px) {
          .color-picker-grid {
            grid-template-columns: 1fr;
          }

          .color-info-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </ToolLayout>
  );
};

export default ColorPicker;




